import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { apiClient, getErrorMessage } from '../../api/client'
import { useOfflineFirstQuery } from '../../hooks/useOfflineFirstQuery'
import { resolveTenantSlug } from '../../lib/tenant'

type PaginatedResponse<T> = {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

type TreatmentPlanListItem = {
  id: number
  client: number
  client_name: string
  version_number: number
  effective_date: string
  end_date: string | null
  review_date: string
  status: string
  status_display: string
  is_active: boolean
  goal_count: number
  created_by_name: string
  created_at: string
}

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'superseded', label: 'Superseded' },
  { value: 'discharged', label: 'Discharged' },
]

export default function TreatmentPlansPage() {
  const tenantSlug = resolveTenantSlug()
  const [searchParams, setSearchParams] = useSearchParams()

  const [status, setStatus] = useState(searchParams.get('status') ?? '')
  const [client, setClient] = useState(searchParams.get('client') ?? '')

  const params = useMemo(() => {
    const p: Record<string, string> = {}
    if (status) p.status = status
    if (client) p.client = client
    return p
  }, [client, status])

  const cacheKey = useMemo(() => {
    return `tenant:${tenantSlug ?? 'unknown'}:treatment_plans:list:status=${status || 'all'}:client=${client || 'all'}`
  }, [client, status, tenantSlug])

  const query = useOfflineFirstQuery<PaginatedResponse<TreatmentPlanListItem>>({
    queryKey: ['treatment-plans', params],
    cacheKey,
    cacheMaxAgeMs: 10 * 60 * 1000,
    queryFn: async () => {
      const res = await apiClient.get<PaginatedResponse<TreatmentPlanListItem>>('/api/v1/treatment-plans/', {
        params,
      })
      return res.data
    },
  })

  const rows = query.data?.results ?? []

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Treatment Plans</h1>
          <p className="text-sm text-gray-600">
            Versioned plans with goals/targets. Only active plans can be used for sessions.
          </p>
        </div>
        <Link to="/app/treatment-plans/new" className="btn-primary whitespace-nowrap">
          New plan
        </Link>
      </div>

      <div className="card p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="label" htmlFor="status">
              Status
            </label>
            <select
              id="status"
              className="input mt-1"
              value={status}
              onChange={(e) => {
                const v = e.target.value
                setStatus(v)
                const next = new URLSearchParams(searchParams)
                if (v) next.set('status', v)
                else next.delete('status')
                setSearchParams(next, { replace: true })
              }}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label" htmlFor="client">
              Client ID
            </label>
            <input
              id="client"
              className="input mt-1"
              value={client}
              onChange={(e) => {
                const v = e.target.value
                setClient(v)
                const next = new URLSearchParams(searchParams)
                if (v) next.set('client', v)
                else next.delete('client')
                setSearchParams(next, { replace: true })
              }}
              placeholder="Filter by client id"
            />
          </div>

          <div className="flex items-end">
            <button
              type="button"
              className="btn-secondary w-full"
              onClick={() => {
                setStatus('')
                setClient('')
                setSearchParams(new URLSearchParams(), { replace: true })
              }}
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {query.isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {getErrorMessage(query.error)}
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="table-header">Client</th>
                <th className="table-header">Version</th>
                <th className="table-header">Effective</th>
                <th className="table-header">Status</th>
                <th className="table-header">Goals</th>
                <th className="table-header">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {query.isLoading && rows.length === 0 ? (
                Array.from({ length: 10 }).map((_, idx) => (
                  <tr key={idx}>
                    <td className="table-cell"><div className="h-4 w-44 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="table-cell"><div className="h-4 w-10 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="table-cell"><div className="h-4 w-24 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="table-cell"><div className="h-4 w-28 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="table-cell"><div className="h-4 w-12 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="table-cell"><div className="h-4 w-24 bg-gray-200 rounded animate-pulse" /></td>
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="table-cell text-gray-500">
                    No treatment plans found.
                  </td>
                </tr>
              ) : (
                rows.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="table-cell">
                      <Link to={`/app/treatment-plans/${p.id}`} className="font-medium text-gray-900 hover:text-primary-600">
                        {p.client_name}
                      </Link>
                    </td>
                    <td className="table-cell">{p.version_number}</td>
                    <td className="table-cell">{p.effective_date}</td>
                    <td className="table-cell">{p.status_display ?? p.status}</td>
                    <td className="table-cell">{p.goal_count}</td>
                    <td className="table-cell">{p.created_at.slice(0, 10)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
